// Storage Manager for IndexedDB persistence
class StorageManager {
  constructor() {
    this.dbName = 'AIGradingAgentDB';
    this.dbVersion = 1;
    this.db = null;
    this.initPromise = this.initDB();
  }

  async initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Create object stores
        if (!db.objectStoreNames.contains('settings')) {
          db.createObjectStore('settings', { keyPath: 'id' });
        }

        if (!db.objectStoreNames.contains('batchResults')) {
          const batchStore = db.createObjectStore('batchResults', { keyPath: 'id', autoIncrement: true });
          batchStore.createIndex('timestamp', 'timestamp', { unique: false });
          batchStore.createIndex('studentName', 'studentName', { unique: false });
        }

        if (!db.objectStoreNames.contains('gradingHistory')) {
          const historyStore = db.createObjectStore('gradingHistory', { keyPath: 'id', autoIncrement: true });
          historyStore.createIndex('timestamp', 'timestamp', { unique: false });
          historyStore.createIndex('studentName', 'studentName', { unique: false });
        }
      };
    });
  }

  async ensureDB() {
    if (!this.db) {
      await this.initPromise;
    }
    return this.db;
  }

  // Settings Management
  async saveSettings(settings) {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['settings'], 'readwrite');
        const store = transaction.objectStore('settings');
        
        const request = store.put({
          id: 'app_settings',
          ...settings,
          lastUpdated: Date.now()
        });

        request.onsuccess = () => resolve(true);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      // Fallback to localStorage
      try {
        localStorage.setItem('grading_settings', JSON.stringify(settings));
        return true;
      } catch (fallbackError) {
        console.error('LocalStorage fallback failed:', fallbackError);
        return false;
      }
    }
  }

  async loadSettings() {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['settings'], 'readonly');
        const store = transaction.objectStore('settings');
        
        const request = store.get('app_settings');

        request.onsuccess = () => {
          if (request.result) {
            const { id, lastUpdated, ...settings } = request.result;
            resolve(settings);
          } else {
            // Try localStorage fallback
            try {
              const saved = localStorage.getItem('grading_settings');
              resolve(saved ? JSON.parse(saved) : null);
            } catch {
              resolve(null);
            }
          }
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error loading settings:', error);
      // Fallback to localStorage
      try {
        const saved = localStorage.getItem('grading_settings');
        return saved ? JSON.parse(saved) : null;
      } catch {
        return null;
      }
    }
  }

  // Batch Results Management
  async saveBatchResult(result) {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['batchResults'], 'readwrite');
        const store = transaction.objectStore('batchResults');
        
        const resultWithMeta = {
          ...result,
          timestamp: Date.now(),
          id: result.id || `batch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        };

        const request = store.add(resultWithMeta);

        request.onsuccess = () => resolve(resultWithMeta.id);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error saving batch result:', error);
      throw error;
    }
  }

  async getBatchResults(limit = 50, offset = 0) {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['batchResults'], 'readonly');
        const store = transaction.objectStore('batchResults');
        const index = store.index('timestamp');
        
        const request = index.openCursor(null, 'prev');
        const results = [];
        let count = 0;
        let skipped = 0;

        request.onsuccess = (event) => {
          const cursor = event.target.result;
          
          if (cursor) {
            if (skipped < offset) {
              skipped++;
              cursor.continue();
              return;
            }
            
            if (count < limit) {
              results.push(cursor.value);
              count++;
              cursor.continue();
            } else {
              resolve(results);
            }
          } else {
            resolve(results);
          }
        };
        
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error loading batch results:', error);
      return [];
    }
  }

  async deleteBatchResult(id) {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['batchResults'], 'readwrite');
        const store = transaction.objectStore('batchResults');
        
        const request = store.delete(id);

        request.onsuccess = () => resolve(true);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error deleting batch result:', error);
      return false;
    }
  }

  async clearAllBatchResults() {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['batchResults'], 'readwrite');
        const store = transaction.objectStore('batchResults');
        
        const request = store.clear();

        request.onsuccess = () => resolve(true);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error clearing batch results:', error);
      return false;
    }
  }

  // Grading History Management
  async saveGradingHistory(record) {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['gradingHistory'], 'readwrite');
        const store = transaction.objectStore('gradingHistory');
        
        const recordWithMeta = {
          ...record,
          timestamp: Date.now(),
          id: record.id || `history_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        };

        const request = store.add(recordWithMeta);

        request.onsuccess = () => resolve(recordWithMeta.id);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error saving grading history:', error);
      throw error;
    }
  }

  async getGradingHistory(limit = 100, offset = 0) {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['gradingHistory'], 'readonly');
        const store = transaction.objectStore('gradingHistory');
        const index = store.index('timestamp');
        
        const request = index.openCursor(null, 'prev');
        const results = [];
        let count = 0;
        let skipped = 0;

        request.onsuccess = (event) => {
          const cursor = event.target.result;
          
          if (cursor) {
            if (skipped < offset) {
              skipped++;
              cursor.continue();
              return;
            }
            
            if (count < limit) {
              results.push(cursor.value);
              count++;
              cursor.continue();
            } else {
              resolve(results);
            }
          } else {
            resolve(results);
          }
        };
        
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error loading grading history:', error);
      return [];
    }
  }

  // Storage Statistics
  async getStorageStats() {
    try {
      const db = await this.ensureDB();
      const stats = {
        settings: 0,
        batchResults: 0,
        gradingHistory: 0,
        totalSize: 0
      };

      const counts = await Promise.all([
        this.countRecords('settings'),
        this.countRecords('batchResults'),
        this.countRecords('gradingHistory')
      ]);

      stats.settings = counts[0];
      stats.batchResults = counts[1];
      stats.gradingHistory = counts[2];
      stats.totalSize = counts.reduce((a, b) => a + b, 0);

      return stats;
    } catch (error) {
      console.error('Error getting storage stats:', error);
      return null;
    }
  }

  async countRecords(storeName) {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([storeName], 'readonly');
        const store = transaction.objectStore(storeName);
        
        const request = store.count();

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error(`Error counting records in ${storeName}:`, error);
      return 0;
    }
  }

  // Export/Import functionality
  async exportData() {
    try {
      const [settings, batchResults, gradingHistory] = await Promise.all([
        this.loadSettings(),
        this.getBatchResults(1000, 0),
        this.getGradingHistory(1000, 0)
      ]);

      return {
        version: '1.0',
        exportDate: new Date().toISOString(),
        settings,
        batchResults,
        gradingHistory
      };
    } catch (error) {
      console.error('Error exporting data:', error);
      throw error;
    }
  }

  async importData(data) {
    try {
      // Validate data structure
      if (!data.version || !data.exportDate) {
        throw new Error('Invalid data format');
      }

      // Import settings
      if (data.settings) {
        await this.saveSettings(data.settings);
      }

      // Import batch results
      if (Array.isArray(data.batchResults)) {
        for (const result of data.batchResults) {
          await this.saveBatchResult(result);
        }
      }

      // Import grading history
      if (Array.isArray(data.gradingHistory)) {
        for (const record of data.gradingHistory) {
          await this.saveGradingHistory(record);
        }
      }

      return true;
    } catch (error) {
      console.error('Error importing data:', error);
      throw error;
    }
  }
}

// Create singleton instance
const storageManager = new StorageManager();
export default storageManager;
